import { cn } from "@/lib/utils";
import { ticksEixo } from "./escala";
import type { Serie } from "./GraficoColunas";

type Linha = Record<string, string | number | null>;

interface Props {
  dados: Linha[];
  chaveX: string;
  series: Serie[];
  formatarValor: (v: number) => string;
  formatarX?: (v: string) => string;
  /** false = colunas lado a lado (ex.: admissões × desligamentos). */
  empilhado?: boolean;
  /** Largura de cada coluna, em px. */
  largura?: number;
  altura?: number;
}

/**
 * Gráfico de colunas compacto (cards do Headcount): grade hairline sem rótulos
 * de eixo, tooltip com os números ao passar o mouse. A legenda fica no
 * cabeçalho do card.
 */
export const MiniGraficoColunas = ({
  dados,
  chaveX,
  series,
  formatarValor,
  formatarX = (v) => v,
  empilhado = true,
  largura = 26,
  altura = 180,
}: Props) => {
  const valor = (l: Linha, chave: string) => Number(l[chave] ?? 0);
  const total = (l: Linha) => series.reduce((s, x) => s + valor(l, x.chave), 0);
  const maximo = Math.max(0, ...dados.map((l) => (empilhado ? total(l) : Math.max(...series.map((s) => valor(l, s.chave))))));
  const ticks = ticksEixo(maximo);
  const topo = ticks[ticks.length - 1] || 1;
  const pct = (v: number) => `${Math.min(100, (v / topo) * 100)}%`;

  return (
    <div className="flex flex-col gap-1.5 tabular-nums">
      <div className="relative flex border-b border-border" style={{ height: altura }}>
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            aria-hidden
            className="pointer-events-none absolute inset-x-0 border-t border-border-soft"
            style={{ bottom: `${i * 25}%` }}
          />
        ))}
        {dados.map((l, i) => {
          const x = String(l[chaveX]);
          const presentes = series.filter((s) => valor(l, s.chave) > 0);
          const lado = i === 0 ? "left-0" : i === dados.length - 1 ? "right-0" : "left-1/2 -translate-x-1/2";
          return (
            <div key={x} className="group relative flex flex-1 items-end justify-center gap-0.5 hover:bg-muted-soft/60">
              {empilhado ? (
                <span className="flex flex-col-reverse" style={{ width: largura, height: pct(total(l)) }}>
                  {presentes.map((s, k) => (
                    <span
                      key={s.chave}
                      className={cn("w-full border-t-2 border-card", k === presentes.length - 1 && "rounded-t-[3px]")}
                      style={{ flex: `0 0 ${(valor(l, s.chave) / total(l)) * 100}%`, background: s.cor }}
                    />
                  ))}
                </span>
              ) : (
                series.map((s) => (
                  <span
                    key={s.chave}
                    className="rounded-t-[3px]"
                    style={{ width: largura, height: pct(valor(l, s.chave)), background: s.cor }}
                  />
                ))
              )}

              <div
                role="tooltip"
                className={cn(
                  "pointer-events-none absolute top-1 z-20 hidden min-w-[150px] rounded-md border bg-popover px-3 py-2 text-xs shadow-dropdown group-hover:block",
                  lado,
                )}
              >
                <p className="mb-1 font-medium text-foreground">{formatarX(x)}</p>
                {series.map((s) => (
                  <p key={s.chave} className="flex items-center justify-between gap-4 text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-sm" style={{ background: s.cor }} aria-hidden />
                      {s.rotulo}
                    </span>
                    <span className="text-foreground">{formatarValor(valor(l, s.chave))}</span>
                  </p>
                ))}
                {empilhado && series.length > 1 && (
                  <p className="mt-1 flex justify-between gap-4 border-t pt-1 font-medium text-foreground">
                    <span>Total</span>
                    <span>{formatarValor(total(l))}</span>
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex text-[11px] text-muted-foreground">
        {dados.map((l) => (
          <span key={String(l[chaveX])} className="flex-1 truncate px-0.5 text-center">
            {formatarX(String(l[chaveX]))}
          </span>
        ))}
      </div>
    </div>
  );
};
